import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, MapPin, QrCode, Copy, Users, Pencil } from "lucide-react";
import api from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";

export default function Sites() {
  const [sites, setSites] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", location: "", headcount: "" });
  const [editing, setEditing] = useState(null);
  const [qr, setQr] = useState(null);

  const load = () => api.get("/sites").then((r) => setSites(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", location: "", headcount: "" }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, location: s.location || "", headcount: s.headcount ?? "" }); setOpen(true); };

  const save = async () => {
    if (!form.name) return;
    const payload = { name: form.name, location: form.location, headcount: parseInt(form.headcount) || 0 };
    if (editing) await api.patch(`/sites/${editing.id}`, payload);
    else await api.post("/sites", payload);
    toast.success(editing ? "Site updated" : "Site created");
    setOpen(false); setEditing(null); setForm({ name: "", location: "", headcount: "" });
    load();
  };

  const reportUrl = (token) => `${window.location.origin}/report/${token}`;
  const copy = (token) => { navigator.clipboard.writeText(reportUrl(token)); toast.success("QR link copied"); };

  if (!sites) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>;

  return (
    <div>
      <PageHeader title="Sites & QR Codes" subtitle="Each site has a unique QR code for instant frontline reporting.">
        <Button className="rounded-full" onClick={openNew} data-testid="new-site-btn"><Plus className="mr-2 h-4 w-4" /> New Site</Button>
      </PageHeader>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Site" : "Create Site"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Set the site name, location and headcount used for group performance normalisation.</DialogDescription>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Site name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="site-name" /></div>
            <div className="space-y-1.5"><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="site-location" /></div>
            <div className="space-y-1.5"><Label>Headcount</Label><Input type="number" min="0" value={form.headcount} onChange={(e) => setForm({ ...form, headcount: e.target.value })} placeholder="People based at this site" data-testid="site-headcount" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full" data-testid="site-save-btn">{editing ? "Save" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {sites.map((s) => (
          <Card key={s.id} className="rounded-2xl border-border p-5">
            <div className="flex items-start justify-between">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-[hsl(var(--accent))]"><MapPin className="h-5 w-5" /></div>
              <Button size="icon" variant="ghost" className="rounded-full" onClick={() => setQr(s)} data-testid={`show-qr-${s.id}`}><QrCode className="h-4 w-4" /></Button>
            </div>
            <div className="mt-4 font-heading text-base font-semibold">{s.name}</div>
            <div className="text-sm text-muted-foreground">{s.location}</div>
            <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><Users className="h-3 w-3" /> {s.headcount || 0} staff</div>
            <div className="mt-4 flex items-center gap-2">
              <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => copy(s.qr_token)} data-testid={`copy-qr-${s.id}`}><Copy className="mr-1 h-3 w-3" /> Copy link</Button>
              <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => openEdit(s)} data-testid={`edit-site-${s.id}`}><Pencil className="mr-1 h-3 w-3" /> Edit</Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!qr} onOpenChange={(o) => !o && setQr(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{qr?.name}</DialogTitle></DialogHeader>
          {qr && (
            <div className="flex flex-col items-center gap-4 py-2">
              <img alt="QR code" className="h-52 w-52 rounded-xl border border-border bg-white p-2"
                src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(reportUrl(qr.qr_token))}`} />
              <p className="break-all text-center text-xs text-muted-foreground">{reportUrl(qr.qr_token)}</p>
              <Button className="w-full rounded-full" onClick={() => copy(qr.qr_token)}><Copy className="mr-2 h-4 w-4" /> Copy reporting link</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
